import { useEffect, useMemo, useRef, useState } from 'react';
import type { CategoryNode } from '../lib/api';
import { CategoryDot } from './CategoryTag';

/**
 * Escolher categoria digitando.
 *
 * Isto era um `<select>`, e um `<select>` deixa de funcionar quando a lista
 * passa de umas vinte linhas: rolar ate "Supermercado" no meio de setenta
 * categorias e mais devagar que digitar "super". Como a casa pode criar
 * quantas categorias quiser, a lista cresce — e a tela tinha de crescer junto.
 *
 * A busca ignora acento e maiuscula, e olha tambem o nome da categoria-mae:
 * digitar "transporte" acha "Combustivel", que e filha dela. Quem lembra do
 * grupo mas nao do nome exato continua achando.
 *
 * O teclado navega tudo: setas andam, Enter escolhe, Esc desiste. Um seletor
 * que so o mouse alcanca e um seletor que metade das pessoas nao usa.
 */

interface Opcao {
  id: string;
  nome: string;
  caminho: string;
  colorToken: string;
  kind: Lado;
  /** Texto contra o qual a busca compara. */
  busca: string;
}

type Lado = 'INCOME' | 'EXPENSE' | 'TRANSFER';

const GRUPO: Record<Lado, string> = {
  TRANSFER: 'Nao e gasto nem receita',
  EXPENSE: 'Saidas',
  INCOME: 'Entradas',
};

/**
 * O lado do proprio lancamento vem primeiro; os outros ficam abaixo.
 *
 * A lista era filtrada pelo lado do lancamento, o que parece prudente e na
 * pratica fecha a unica porta que interessa: quem abre a coluna da categoria
 * e porque discorda do que esta la. Num lancamento classificado como receita
 * so apareciam "Salario" e "Honorarios" — e se a pessoa sabe que aquilo foi
 * a fatura do cartao, nao havia como dizer.
 *
 * Filtrar deu lugar a ORDENAR: o lado provavel abre a lista, e os outros
 * continuam ao alcance de quem rolar ou digitar.
 */
function ordemDe(preferido: Lado | null): Record<Lado, number> {
  const padrao: Record<Lado, number> = { EXPENSE: 1, INCOME: 2, TRANSFER: 3 };
  if (!preferido) return padrao;
  return { ...padrao, [preferido]: 0 };
}

export function SeletorDeCategoria({
  categorias,
  kind,
  valor,
  aoEscolher,
  aoDesistir,
  autoFocus = true,
  placeholder = 'digite para buscar...',
}: {
  categorias: CategoryNode[];
  /**
   * O lado que abre a lista. Todos continuam disponiveis abaixo dele.
   *
   * Nunca filtra: trocar a categoria e justamente como se corrige um lado
   * errado, e esconder os outros grupos transformaria a correcao mais
   * comum numa que nao tem onde ser feita.
   */
  kind: Lado | null;
  valor?: string | null;
  aoEscolher: (categoryId: string, kind: Lado) => void;
  aoDesistir?: () => void;
  autoFocus?: boolean;
  placeholder?: string;
}) {
  const [termo, setTermo] = useState('');
  const [destacada, setDestacada] = useState(0);
  const listaRef = useRef<HTMLUListElement>(null);

  const opcoes = useMemo(() => achatar(categorias, kind), [categorias, kind]);
  // Com todos os grupos na lista, o cabecalho sempre ajuda a separa-los.
  const mostrarGrupos = true;

  const filtradas = useMemo(() => {
    const alvo = comparavel(termo);
    if (alvo === '') return opcoes;
    return opcoes.filter((opcao) => opcao.busca.includes(alvo));
  }, [opcoes, termo]);

  // A opcao destacada nao pode ficar fora da lista depois que ela encolhe.
  useEffect(() => setDestacada(0), [termo]);

  useEffect(() => {
    const item = listaRef.current?.children[destacada];
    if (item instanceof HTMLElement) item.scrollIntoView({ block: 'nearest' });
  }, [destacada]);

  const escolher = (indice: number) => {
    const opcao = filtradas[indice];
    if (opcao) aoEscolher(opcao.id, opcao.kind);
  };

  return (
    <div className="seletor">
      <input
        className="edit-inline"
        type="text"
        role="combobox"
        aria-expanded
        aria-controls="lista-de-categorias"
        aria-label="Buscar categoria"
        autoFocus={autoFocus}
        value={termo}
        placeholder={placeholder}
        onChange={(evento) => setTermo(evento.target.value)}
        onKeyDown={(evento) => {
          if (evento.key === 'ArrowDown') {
            evento.preventDefault();
            setDestacada((atual) => Math.min(atual + 1, filtradas.length - 1));
          }
          if (evento.key === 'ArrowUp') {
            evento.preventDefault();
            setDestacada((atual) => Math.max(atual - 1, 0));
          }
          if (evento.key === 'Enter') {
            evento.preventDefault();
            escolher(destacada);
          }
          if (evento.key === 'Escape') aoDesistir?.();
        }}
        // O clique numa opcao tira o foco do campo. Sem o atraso, a lista
        // fecharia antes de o clique chegar ao item.
        onBlur={() => window.setTimeout(() => aoDesistir?.(), 150)}
      />

      <ul className="seletor-lista" id="lista-de-categorias" role="listbox" ref={listaRef}>
        {filtradas.length === 0 && (
          <li className="seletor-vazio">
            Nenhuma categoria com "{termo}". Crie uma em Categorias.
          </li>
        )}
        {filtradas.map((opcao, indice) => (
          <li key={opcao.id}>
            {/* O cabecalho do grupo aparece quando os dois lados estao na
                lista: sem ele, "Salario" e "Supermercado" ficariam lado a
                lado sem nada dizendo por que. */}
            {mostrarGrupos && opcao.kind !== filtradas[indice - 1]?.kind && (
              <span className="seletor-grupo">{GRUPO[opcao.kind]}</span>
            )}
            <button
              type="button"
              role="option"
              aria-selected={opcao.id === valor}
              className={`seletor-item${indice === destacada ? ' destacada' : ''}`}
              onMouseEnter={() => setDestacada(indice)}
              onMouseDown={(evento) => {
                // mousedown, e nao click: o blur do campo chega antes do
                // click e fecharia a lista debaixo do dedo.
                evento.preventDefault();
                aoEscolher(opcao.id, opcao.kind);
              }}
            >
              <CategoryDot colorToken={opcao.colorToken} />
              <span>{opcao.nome}</span>
              {opcao.caminho !== opcao.nome && (
                <span className="seletor-caminho">{opcao.caminho}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A arvore vira lista plana, com mae e filha no mesmo nivel de escolha.
 *
 * Categoria-mae continua escolhivel: nem todo gasto merece subcategoria, e
 * obrigar a descer um nivel so faria a pessoa escolher qualquer uma.
 */
function achatar(categorias: CategoryNode[], kind: Lado | null): Opcao[] {
  const saida: Opcao[] = [];

  const ordem = ordemDe(kind);
  const ordenadas = [...categorias].sort((a, b) => ordem[a.kind] - ordem[b.kind]);

  for (const mae of ordenadas) {
    if (mae.archived) continue;

    saida.push({
      id: mae.id,
      nome: mae.name,
      caminho: mae.name,
      colorToken: mae.colorToken,
      kind: mae.kind,
      busca: comparavel(mae.name),
    });

    for (const filha of mae.children) {
      if (filha.archived) continue;
      saida.push({
        id: filha.id,
        nome: filha.name,
        caminho: `${mae.name} / ${filha.name}`,
        colorToken: filha.colorToken,
        kind: filha.kind,
        // A mae entra na busca: quem lembra do grupo acha a filha.
        busca: comparavel(`${mae.name} ${filha.name}`),
      });
    }
  }

  return saida;
}

export function comparavel(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}
